import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  type SetStateAction,
} from 'react'
import { useQueryClient } from 'react-query'

import { TRUST_AUTH_TOKENS_KEY } from '../constants/storage'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { api } from '../services/api'
import type UserDomain from '../types/domain/user-domain'
import { Spinner } from '../components/Spinner'

type UserType = Pick<UserDomain, 'type'>['type']

interface AuthContextData {
  user: UserDomain | null
  signIn: (
    credentials: Credentials,
    interceptor?: (userType: UserType) => boolean
  ) => Promise<UserDomain>
  signOut: () => void
  fetchUser: () => Promise<void>
  setUser: React.Dispatch<SetStateAction<UserDomain | null>>
}

interface AuthProviderProps {
  children: ReactNode
}

interface Credentials {
  email: string
  password: string
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserDomain | null>(null)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const queryClient = useQueryClient()

  // eslint-disable-next-line
  const [_, setAuthTokens] = useLocalStorage<{
    accessToken: string
    refreshToken: string
  } | null>(TRUST_AUTH_TOKENS_KEY, null)

  const signIn = async (
    credentials: Credentials,
    interceptor?: (authUserType: UserType) => boolean
  ) => {
    const response = await api.post('/auth/login', credentials)
    const { user, accessToken, refreshToken } = response.data

    if (typeof interceptor === 'function') {
      const canContinue = interceptor(user.type)

      if (!canContinue) {
        return false
      }
    }

    setUser(user)
    setAuthTokens({ accessToken, refreshToken })
    api.defaults.headers.common.Authorization = `Bearer ${accessToken}`

    return user
  }

  const signOut = () => {
    setUser(null)
    localStorage.removeItem(TRUST_AUTH_TOKENS_KEY)
    localStorage.removeItem('filters')
    localStorage.removeItem('databaseInfoPerPageNumber')
    api.defaults.headers.common.Authorization = ''
    queryClient.removeQueries()
  }

  const fetchUser = async () => {
    setIsAuthenticating(true)

    try {
      const response = await api.get('/users/me')
      setUser(response.data)
    } catch (error) {
      localStorage.removeItem(TRUST_AUTH_TOKENS_KEY)
      api.defaults.headers.common.Authorization = ''
      setUser(null)
    } finally {
      setIsAuthenticating(false)
    }
  }

  const isAuthorizationSet = () => {
    const authorization = api.defaults.headers.common.Authorization as string

    if (!authorization || authorization.trim() === '') {
      return false
    }

    return !!authorization?.replace('Bearer ', '').split('.').length
  }

  useEffect(() => {
    if (isAuthorizationSet()) {
      fetchUser()
    }
  }, [api])

  useEffect(() => {
    const handleInvalidToke = (e: any) => {
      if (e.key === TRUST_AUTH_TOKENS_KEY && !e.newValue) {
        signOut()
      }
    }

    window.addEventListener('storage', handleInvalidToke)

    return () => {
      window.removeEventListener('storage', handleInvalidToke)
    }
  }, [signOut])

  if (!user && !isAuthenticating && isAuthorizationSet()) {
    setIsAuthenticating(true)
  }

  if (isAuthenticating && !user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signOut,
        fetchUser,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  return useContext(AuthContext)
}
